import { type ReactElement, useId } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import {
  FormControl,
  type FormControlProps,
  type FormHelperTextProps,
  type InputLabelProps,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuItemProps,
  type SelectProps as MuiSelectProps,
} from '@mui/material';

import {
  StyledFormHelperText,
  StyledInputLabel,
  StyledSelect,
} from './components';

export type SelectOption<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
> = Record<LabelKey, Label> &
  Record<ValueKey, Value> &
  Record<PropertyKey, unknown> & {
    disabled?: boolean;
  };

type DefaultLabelValueKeys<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
> = {
  label: LabelKey;
  value: ValueKey;
};

export type CustomSelectProps<
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
> = {
  helperText?: string;
  FormControlProps?: FormControlProps;
  InputLabelProps?: InputLabelProps;
  FormHelperTextProps?: FormHelperTextProps;
  options: Array<SelectOption<LabelKey, ValueKey, Label, Value>>;
  optionKeys: DefaultLabelValueKeys<LabelKey, ValueKey>;
  placeholder?: React.ReactNode;
} & MuiSelectProps;

export const CustomSelect = <
  LabelKey extends PropertyKey,
  ValueKey extends PropertyKey,
  Label extends number | string,
  Value extends NonNullable<MenuItemProps['value']>,
>({
  FormControlProps,
  InputLabelProps,
  FormHelperTextProps,
  options,
  optionKeys,
  placeholder,
  helperText,
  label,
  ...props
}: CustomSelectProps<LabelKey, ValueKey, Label, Value>): ReactElement => {
  const componentId = useId();

  return (
    <FormControl fullWidth {...FormControlProps}>
      {label && (
        <StyledInputLabel
          shrink
          required={props.required}
          htmlFor={props.id ?? componentId}
          {...InputLabelProps}
        >
          {label}
        </StyledInputLabel>
      )}

      <StyledSelect
        id={componentId}
        displayEmpty={!props.value}
        renderValue={(selected) => {
          if (
            selected === undefined ||
            (typeof selected === 'string' && selected.length === 0)
          )
            return placeholder;

          const selectedValue = options.find(
            (el) => el[optionKeys.value] === selected
          );

          return selectedValue?.[optionKeys.label];
        }}
        {...props}
      >
        {options.map((option) => {
          const label = option[optionKeys.label];
          const value = option[optionKeys.value];
          const isDisabled = Boolean(option.disabled);
          const isSelected = value === props.value;

          return (
            <MenuItem key={value} value={value} disabled={isDisabled}>
              <ListItemText
                primaryTypographyProps={{
                  fontSize: 13,
                }}
              >
                {label}
              </ListItemText>

              {isSelected && (
                <ListItemIcon
                  sx={{
                    justifyContent: 'flex-end',
                  }}
                >
                  <CheckIcon fontSize='large'/>
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </StyledSelect>

      {helperText && (
        <StyledFormHelperText error={props.error} {...FormHelperTextProps}>
          {helperText}
        </StyledFormHelperText>
      )}
    </FormControl>
  );
};
